import React, { useState, forwardRef, memo, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Typography,
  Box,
  Avatar,
  Grid,
  Slide,
  useMediaQuery,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import { Close as CloseIcon } from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import {
  updateVariants,
  hoverVariants,
  springTransition,
  pulseKeyframes,
} from "../../animations";
import { alpha } from "@mui/material/styles";

const StyledAvatar = styled(motion.div)(({ theme }) => ({
  width: theme.spacing(7),
  height: theme.spacing(7),
  marginBottom: theme.spacing(1),
}));

const AmountButton = styled(motion.button)(({ theme }) => ({
  width: "100%",
  padding: theme.spacing(1),
  border: `1px solid ${theme.palette.primary.main}`,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: "transparent",
  color: theme.palette.primary.main,
  cursor: "pointer",
  fontSize: "1rem",
  "&.selected": {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
}));

const MotionBox = styled(motion.div)({
  width: "100%",
});

const Transition = forwardRef((props, ref) => {
  return <Slide direction="up" ref={ref} {...props} />;
});

const AmountButtonGroup = memo(({ tipAmounts, selectedAmount, onSelect }) => (
  <Grid
    container
    spacing={1}
    sx={{ mb: 2 }}
    role="radiogroup"
    aria-label="チップ金額の選択"
  >
    {tipAmounts.map((value, index) => (
      <Grid item xs={6} sm={4} key={value}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.1 }}
        >
          <AmountButton
            onClick={() => onSelect(value)}
            className={selectedAmount === value ? "selected" : ""}
            variants={hoverVariants}
            whileHover="hover"
            whileTap="tap"
            role="radio"
            aria-checked={selectedAmount === value}
          >
            ¥{value.toLocaleString()}
          </AmountButton>
        </motion.div>
      </Grid>
    ))}
  </Grid>
));

const StaffInfo = memo(({ staff }) => (
  <motion.div
    key={staff.id}
    variants={updateVariants}
    initial="initial"
    animate="animate"
    exit="exit"
  >
    <StyledAvatar whileHover={{ scale: 1.1 }} transition={springTransition}>
      <Avatar
        src={staff.avatarUrl}
        alt={staff.name}
        aria-label={`${staff.name}のプロフィール画像`}
        sx={{ width: "100%", height: "100%" }}
      />
    </StyledAvatar>
    <Typography variant="h6">{staff.name}</Typography>
    {staff.position && (
      <Typography variant="body2" color="text.secondary">
        {staff.position}
      </Typography>
    )}
  </motion.div>
));

const TipDialog = ({ open, onClose, staff, onSubmit }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [amount, setAmount] = useState(100);
  const [message, setMessage] = useState("");
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const tipAmounts = [100, 300, 500, 1000, 2000, 5000];

  const handleSubmit = useCallback(async () => {
    try {
      setIsSubmitting(true);
      setSubmitStatus("submitting");

      await onSubmit({
        amount,
        message,
        isAnonymous,
        staffId: staff?.id,
      });

      setSubmitStatus("success");
      setTimeout(() => {
        handleClose();
        setSubmitStatus(null);
      }, 1000);
    } catch (error) {
      setSubmitStatus("error");
      setTimeout(() => {
        setSubmitStatus(null);
      }, 2000);
    } finally {
      setIsSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amount, message, isAnonymous, staff, onSubmit]);

  const handleClose = useCallback(() => {
    setAmount(100);
    setMessage("");
    setIsAnonymous(false);
    setSelectedAmount(null);
    onClose();
  }, [onClose]);

  const handleAmountSelect = useCallback((value) => {
    setAmount(value);
    setSelectedAmount(value);
  }, []);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={fullScreen}
      TransitionComponent={Transition}
      aria-labelledby="tip-dialog-title"
      aria-describedby="tip-dialog-description"
      PaperProps={{
        sx: {
          position: "relative",
          "& .submit-overlay": {
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: theme.zIndex.modal + 1,
          },
          "& .success-animation": {
            animation: `${pulseKeyframes} 0.5s ease-in-out`,
            backgroundColor: alpha(theme.palette.success.main, 0.1),
          },
          "& .error-animation": {
            animation: `${theme.transitions.create("all")} 0.3s ease-in-out`,
            backgroundColor: alpha(theme.palette.error.main, 0.1),
          },
        },
      }}
    >
      {isSubmitting && (
        <motion.div
          className="submit-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <CircularProgress size={48} />
        </motion.div>
      )}

      {submitStatus === "success" && (
        <motion.div
          className="success-animation"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              textAlign: "center",
              zIndex: theme.zIndex.modal + 2,
            }}
          >
            <Typography variant="h6" color="success.main">
              送信完了
            </Typography>
          </Box>
        </motion.div>
      )}

      {submitStatus === "error" && (
        <motion.div
          className="error-animation"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              textAlign: "center",
              zIndex: theme.zIndex.modal + 2,
            }}
          >
            <Typography variant="h6" color="error">
              エラーが発生しました
            </Typography>
          </Box>
        </motion.div>
      )}

      <DialogTitle id="tip-dialog-title">
        <Box display="flex" alignItems="center" justifyContent="space-between">
          スタッフにチップを送る
          <IconButton
            edge="end"
            color="inherit"
            onClick={handleClose}
            aria-label="閉じる"
            component={motion.button}
            whileHover={{ rotate: 90 }}
            transition={springTransition}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            textAlign: "center",
            mb: 2,
            role: "region",
            "aria-label": "スタッフ情報",
          }}
        >
          <AnimatePresence mode="wait">
            {staff && <StaffInfo staff={staff} />}
          </AnimatePresence>
        </Box>

        <MotionBox
          variants={updateVariants}
          initial="initial"
          animate="animate"
          transition={{ delay: 0.2 }}
        >
          <Typography gutterBottom role="heading" aria-level="2">
            金額を選択
          </Typography>
          <AmountButtonGroup
            tipAmounts={tipAmounts}
            selectedAmount={selectedAmount}
            onSelect={handleAmountSelect}
          />
        </MotionBox>

        <MotionBox
          variants={updateVariants}
          initial="initial"
          animate="animate"
          transition={{ delay: 0.3 }}
        >
          <Typography gutterBottom role="heading" aria-level="2">
            または金額を入力
          </Typography>
          <TextField
            type="number"
            value={amount}
            onChange={(e) => setAmount(Number(e.target.value))}
            fullWidth
            InputProps={{
              inputProps: { min: 100, "aria-label": "チップ金額" },
            }}
            sx={{ mb: 2 }}
            helperText="最低金額は100円です"
          />
        </MotionBox>

        <MotionBox
          variants={updateVariants}
          initial="initial"
          animate="animate"
          transition={{ delay: 0.4 }}
        >
          <TextField
            label="メッセージを入力（任意）"
            multiline
            rows={3}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
            inputProps={{
              "aria-label": "スタッフへのメッセージ",
              maxLength: 200,
            }}
            helperText={`${message.length}/200文字`}
          />

          <FormControlLabel
            control={
              <Switch
                checked={isAnonymous}
                onChange={(e) => setIsAnonymous(e.target.checked)}
                inputProps={{ "aria-label": "匿名で送信" }}
              />
            }
            label="匿名で送る"
          />
        </MotionBox>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
          <Button
            onClick={handleClose}
            aria-label="キャンセル"
            disabled={isSubmitting}
          >
            キャンセル
          </Button>
        </motion.div>
        <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={amount < 100 || isSubmitting}
            aria-label={`${amount}円のチップを送信`}
          >
            {isSubmitting ? "送信中..." : "送信"}
          </Button>
        </motion.div>
      </DialogActions>
    </Dialog>
  );
};

export default memo(TipDialog);
